import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { nieuwUitStaat } from "../lib/slashopdrachten";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, BookOpen, Plus, Pencil, Upload, Trash2, FileText, Search, Loader2, CheckCircle, XCircle, FolderInput, RefreshCw, PauseCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import DocumentViewer from "../components/DocumentViewer";
import KindBadge, { FACTS, REFERENCE } from "../components/KindBadge";
import FolderSyncStatus from "../components/FolderSyncStatus";
import AliassenPaneel from "../components/AliassenPaneel";
import NietIngelezenPaneel from "../components/NietIngelezenPaneel";
import { useTaal } from "@/contexts/TaalContext";


export default function KnowledgePage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const [bases, setBases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newProjectId, setNewProjectId] = useState("");
  const [newKind, setNewKind] = useState(FACTS);
  // The id of the base being edited, or empty for a new one. One dialog serves
  // both: two dialogs drift apart, and drift here means exactly the state this
  // screen was in until today, where a kind could be chosen at creation and
  // never again.
  const [editingId, setEditingId] = useState("");
  const [uploading, setUploading] = useState(false);
  // De mapimport van BLOK 8 fase 3. Het verslag blijft staan nadat de import
  // klaar is, want een melding die na drie seconden verdwijnt is geen verslag,
  // en juist de MISLUKKINGEN wil je rustig kunnen nalezen.
  const [folderPath, setFolderPath] = useState("");
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [query, setQuery] = useState("");
  const [queryResults, setQueryResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/knowledge/bases`).then(r => setBases(r.data)).catch(() => {}),
      axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const loadDocuments = async (kbId) => {
    try {
      const res = await axios.get(`${API}/knowledge/bases/${kbId}/documents`);
      setDocuments(res.data);
    } catch { setDocuments([]); }
  };

  const selectBase = async (kb) => {
    setSelectedBase(kb);
    setQueryResults(null);
    // Het pad van deze bank, zodat "opnieuw vectoriseren" ergens heen kan en
    // het veld niet het pad van de vorige bank toont.
    setFolderPath(kb.source_folder || "");
    setImportReport(null);
    await loadDocuments(kb.id);
  };

  const saveBase = async () => {
    if (!newName.trim()) return;
    try {
      if (editingId) {
        // Only what this dialog can change. The owner is not in here: moving a
        // base between a project and yourself changes which chats search it,
        // and that is a different decision with different consequences.
        const res = await axios.patch(`${API}/knowledge/bases/${editingId}`, {
          name: newName, description: newDesc, kind: newKind,
        });
        setBases(prev => prev.map(b => (b.id === editingId ? res.data : b)));
        // The open base is a second copy of the same row. Leaving it stale
        // would show the old name in the header while the list shows the new.
        setSelectedBase(prev => (prev && prev.id === editingId ? res.data : prev));
        toast.success(t("Kennisbank bijgewerkt"));
      } else {
        // No project chosen means a global base: one that hangs from the person
        // and weighs in on every chat. `project_id` is left out rather than sent
        // empty, because the backend refuses a base that claims both owners and
        // an empty string is not the same statement as "no project".
        const body = { name: newName, description: newDesc, kind: newKind };
        if (newProjectId) body.project_id = newProjectId;
        const res = await axios.post(`${API}/knowledge/bases`, body);
        setBases(prev => [...prev, res.data]);
        toast.success(t("Kennisbank aangemaakt"));
      }
      setCreateOpen(false);
      setNewName("");
      setNewDesc("");
      setNewKind(FACTS);
      setEditingId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || (editingId ? t("Opslaan is mislukt") : t("Aanmaken is mislukt")));
    }
  };

  const deleteBase = async (kbId) => {
    try {
      await axios.delete(`${API}/knowledge/bases/${kbId}`);
      setBases(prev => prev.filter(b => b.id !== kbId));
      if (selectedBase?.id === kbId) { setSelectedBase(null); setDocuments([]); }
      toast.success(t("Kennisbank verwijderd"));
    } catch { toast.error(t("Verwijderen is mislukt")); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedBase) return;
    setUploading(true);
    const form = new FormData();
    files.forEach(f => form.append("files", f));
    try {
      const res = await axios.post(`${API}/knowledge/bases/${selectedBase.id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments(prev => [...res.data, ...prev]);
      toast.success(`${files.length} document(en) geüpload`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Uploaden is mislukt"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  /**
   * Read a folder of notes into this base. BLOK 8 fase 3.
   *
   * A typed path and not a picker, and that is a limitation worth naming: a
   * web page cannot hand the server a folder location, and `webkitdirectory`
   * gives it the files instead, which would upload a whole vault through the
   * browser. The backend runs on this machine, so it reads the folder itself.
   * A native picker belongs in the Electron shell and is not in this phase.
   *
   * The REPORT is the outcome, never a green tick. An import of 1285 files
   * with three broken ones must not read as "done", so the failures are shown
   * with their reason.
   */
  const importFolder = async () => {
    const pad = folderPath.trim();
    if (!pad || !selectedBase) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/knowledge/bases/${selectedBase.id}/import-folder`, { folder: pad });
      setImportReport(res.data);
      // ALLEEN de documentenlijst verversen, niet de hele bank opnieuw kiezen.
      // `selectBase` wist het verslag, want dat hoort te gebeuren als je NAAR
      // EEN ANDERE bank gaat. Hier gaan we nergens heen, en de eerste versie
      // hiervan zette het verslag en gooide het in dezelfde adem weg: het was
      // nooit te zien. Gevonden door de wachter en niet op glas.
      await loadDocuments(selectedBase.id);
      const r = res.data;
      toast.success(`${r.toegevoegd} toegevoegd, ${r.bijgewerkt} bijgewerkt, ${r.overgeslagen} ongewijzigd`);
      if (r.mislukt?.length) toast.error(`${r.mislukt.length} bestand(en) konden niet worden gelezen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Importeren is mislukt"));
    } finally {
      setImporting(false);
    }
  };

  /** The same road again, so a second run is seconds rather than minutes. */
  const reindex = async () => {
    if (!selectedBase) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/knowledge/bases/${selectedBase.id}/reindex`);
      setImportReport(res.data);
      // ALLEEN de documentenlijst verversen, niet de hele bank opnieuw kiezen.
      // `selectBase` wist het verslag, want dat hoort te gebeuren als je NAAR
      // EEN ANDERE bank gaat. Hier gaan we nergens heen, en de eerste versie
      // hiervan zette het verslag en gooide het in dezelfde adem weg: het was
      // nooit te zien. Gevonden door de wachter en niet op glas.
      await loadDocuments(selectedBase.id);
      const r = res.data;
      toast.success(`${r.bijgewerkt} bijgewerkt, ${r.overgeslagen} ongewijzigd`);
      if (r.mislukt?.length) toast.error(`${r.mislukt.length} bestand(en) konden niet worden gelezen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Opnieuw indexeren is mislukt"));
    } finally {
      setImporting(false);
    }
  };

  /**
   * De automatische synchronisatie voor deze bank aan of uit. R18.
   *
   * De ANTWOORDRIJ wordt overgenomen en niet de waarde die we net stuurden.
   * Dat is het verschil tussen tonen wat er is opgeslagen en tonen wat we
   * hoopten; als de route iets anders doet dan gevraagd, hoort het scherm dat
   * te laten zien en niet te verbergen. Bovendien draagt die rij een verse
   * `last_sync_result`, en die hoort bij de tijdstempel ernaast.
   */
  const toggleSync = async () => {
    if (!selectedBase) return;
    try {
      const res = await axios.patch(`${API}/knowledge/bases/${selectedBase.id}`, {
        sync_off: !selectedBase.sync_off,
      });
      setSelectedBase(res.data);
      setBases(bs => bs.map(b => (b.id === res.data.id ? res.data : b)));
      toast.success(res.data.sync_off
        ? t("Automatisch bijwerken staat uit voor deze kennisbank")
        : t("Automatisch bijwerken staat aan voor deze kennisbank"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Automatisch bijwerken kon niet worden omgezet"));
    }
  };

  // W1k (besluit Frank 24 okt 2026): intern. Ook hier de antwoordrij overnemen.
  const toggleIntern = async () => {
    if (!selectedBase) return;
    try {
      const res = await axios.patch(`${API}/knowledge/bases/${selectedBase.id}`, { intern: !selectedBase.intern });
      setSelectedBase(res.data);
      setBases(bs => bs.map(b => (b.id === res.data.id ? res.data : b)));
      toast.success(res.data.intern
        ? t("Intern: deze inhoud gaat niet naar een koppeling buiten de EU")
        : t("Niet meer intern"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Intern kon niet worden omgezet"));
    }
  };

  /** Undo a "remove from project", from where the document actually lives. */
  const putBack = async (doc) => {
    try {
      await axios.post(`${API}/projects/${doc.project_id}/documents/${doc.id}`);
      setDocuments(prev => prev.map(d => (d.id === doc.id ? { ...d, in_project: true } : d)));
      toast.success(`${doc.filename} telt weer mee in ${doc.project_name || "het project"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Terugzetten is mislukt"));
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/knowledge/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success(t("Document verwijderd"));
    } catch { toast.error(t("Verwijderen is mislukt")); }
  };

  /** Which scope a base counts in. The backend says so; the fallback is for a
   *  response from before phase 2, where a project was the only possibility. */
  const isGlobal = (kb) => (kb.scope ? kb.scope === "global" : !kb.project_id);

  const globalBases = bases.filter(isGlobal);
  const projectBases = bases.filter(kb => !isGlobal(kb));

  // SL-a: /kennisbank <naam> opent dit formulier met de naam ingevuld.
  const locatie = useLocation();
  useEffect(() => {
    const naam = nieuwUitStaat(locatie.state);
    if (naam !== null) { openCreate(""); setNewName(naam); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locatie.key]);

  const openCreate = (projectId) => {
    setEditingId("");
    setNewProjectId(projectId);
    setNewName("");
    setNewDesc("");
    setNewKind(FACTS);
    setCreateOpen(true);
  };

  /** The same dialog, filled in with what this base already is.
   *
   *  `kb.kind || FACTS` and not FACTS outright. Opening this on a reference
   *  base and pressing save would otherwise turn it into a facts base without
   *  anyone touching that field, and the next chat would start searching the
   *  formats again with nothing on screen to show why. */
  const openEdit = (kb) => {
    setEditingId(kb.id);
    setNewProjectId(kb.project_id || "");
    setNewName(kb.name || "");
    setNewDesc(kb.description || "");
    setNewKind(kb.kind || FACTS);
    setCreateOpen(true);
  };

  /** One row in the list. Shared by both groups so they cannot drift apart. */
  const renderBase = (kb) => (
    <div key={kb.id} className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedBase?.id === kb.id ? 'bg-accent/10 border border-accent/30' : 'hover:bg-muted/50 border border-transparent'}`} onClick={() => selectBase(kb)}>
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <h3 className="font-medium text-sm truncate">{kb.name}</h3>
          {/* Only a reference base is marked, and the badge sits outside the
              truncating name so a long name cannot push it off the row. */}
          <KindBadge kb={kb} />
        </div>
        {/* Both buttons are always visible, and that is the whole of punt L.
            The bin was `opacity-0` with a hover reveal and no breakpoint until
            27 July, so on a phone it existed and could never be seen; that
            sweep gave it `opacity-100 md:opacity-0`, which fixed the phone and
            left the desktop needing a pointer on something invisible. The
            pencil arrived on 21 August always visible, and two neighbouring
            buttons behaving differently is what Frank hit on glass: he went
            looking for the bin and could not find it. They now dress the same.
            Guarded per button in KnowledgePage.knoppen.test.js, so a third
            button added here falls under the same rule. */}
        <div className="flex items-center shrink-0">
          <Button variant="ghost" size="icon" aria-label={`Bewerk ${kb.name}`} data-testid={`edit-base-${kb.id}`} className="tap-target h-8 w-8 md:h-6 md:w-6" onClick={e => { e.stopPropagation(); openEdit(kb); }}>
            <Pencil className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" aria-label={`Verwijder ${kb.name}`} data-testid={`delete-base-${kb.id}`} className="tap-target h-8 w-8 md:h-6 md:w-6" onClick={e => { e.stopPropagation(); deleteBase(kb.id); }}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {/* This line read `kb.project_id.slice(0, 8)`. A global base has no
          project, so the first one ever created threw here and took the whole
          list with it. The gap arrived with the phase 2 backend and could not
          be seen until a global base existed. Test:
          KnowledgePage.scope.test.js. */}
      <p className="text-xs text-muted-foreground truncate">
        {kb.description || (isGlobal(kb) ? t("Telt mee in elk gesprek") : kb.project_id?.slice(0, 8) || "")}
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        {kb.document_count === 1 ? t("1 document") : t("{n} documenten", { n: kb.document_count })}
        {/* PUNT 40, Franks eis van 30 augustus 2026. Het aantal onleesbare
            bestanden hoort op de KAART en niet alleen binnen de bank. Op 30
            augustus stonden er 32 mislukte documenten tussen 2353 goede, en
            het enige spoor was een rood kruis diep in een tabel; wie de bank
            niet opende zag niets. */}
        {kb.unreadable_count > 0 ? (
          <span className="text-destructive" data-testid={`kb-unreadable-${kb.id}`}>
            {' '}&middot; {t("{n} onleesbaar", { n: kb.unreadable_count })}
          </span>
        ) : null}
      </p>
    </div>
  );

  const handleSearch = async () => {
    if (!query.trim() || !selectedBase) return;
    setSearching(true);
    try {
      const res = await axios.post(`${API}/knowledge/query`, { kb_id: selectedBase.id, query, top_k: 5 });
      setQueryResults(res.data);
    } catch { toast.error(t("Zoeken is mislukt")); }
    finally { setSearching(false); }
  };

  return (
    <div className={`h-screen flex flex-col bg-background ${BOTTOM_NAV_SPACER}`}>
      {/* Proef blok-k (19 okt): op 390 pixels stak "Nieuwe kennisbank" buiten beeld. */}
      <header className="flex flex-wrap items-center px-4 gap-3 pb-2 border-b min-h-12 pt-[var(--veilig-boven)]" data-testid="kennis-kop">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="tap-target h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-serif text-lg font-light">
          <BookOpen className="h-4 w-4 inline mr-2 text-[hsl(var(--accent))]" />
          {t("Kennisbanken")}
        </h1>
        <Dialog open={createOpen} onOpenChange={open => { setCreateOpen(open); if (!open) setEditingId(""); }}>
          {/* This was a DialogTrigger, which opens the dialog and nothing else.
              That was fine while the dialog only ever created; now that it also
              edits, opening it without clearing `editingId` meant New Base
              reopened the base you edited last and saving it renamed that one.
              So the button calls openCreate itself, and closing the dialog
              clears the edit as well: a dialog dismissed with Escape must not
              leave the screen holding a half finished intention. */}
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => openCreate("")}><Plus className="h-3 w-3 mr-1" />{t("Nieuwe kennisbank")}</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? t("Kennisbank bewerken") : t("Kennisbank aanmaken")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label htmlFor="kb-name">{t("Naam")}</Label><Input id="kb-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder={t("bijv. Projectdocumenten")} /></div>
              <div><Label htmlFor="kb-desc">{t("Omschrijving")}</Label><Input id="kb-desc" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder={t("Optioneel")} /></div>
              {/* Where a base hangs is fixed once it exists. Moving it between a
                  project and yourself changes which chats search it, so it is
                  not something to change by reopening a dialog. */}
              {editingId ? null : (
                <div><Label htmlFor="kb-project">{t("Project")}</Label>
                  {/* Global is the first option and it is spelled out, because
                      the empty value used to mean "you have not chosen yet" and
                      now means "this belongs to you and applies everywhere". A
                      blank first option would leave someone creating a global
                      base without noticing they had. */}
                  <select id="kb-project" value={newProjectId} onChange={e => setNewProjectId(e.target.value)} className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm">
                    <option value="">{t("Algemeen (alle gesprekken)")}</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <div><Label htmlFor="kb-kind">{t("Bevat")}</Label>
                {/* Until today this could only be set with a hand written
                    UPDATE against the live database. The two words are spelled
                    out with what they do, because "reference" on its own does
                    not tell anyone that such a base leaves the chat sweep. */}
                <select id="kb-kind" data-testid="kb-kind-select" value={newKind} onChange={e => setNewKind(e.target.value)} className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm">
                  <option value={FACTS}>{t("Feiten (doorzocht in elk gesprek)")}</option>
                  <option value={REFERENCE}>{t("Naslag (vormen en uitgewerkte voorbeelden)")}</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {newKind === REFERENCE
                    ? t("Wordt niet doorzocht in gesprekken. Een agent die deze kennisbank noemt, krijgt een tweede beurt die zijn antwoord in deze vorm giet.")
                    : t("Wordt bij elk gesprek doorzocht, in dit project of overal, afhankelijk van waar hij hangt.")}
                </p>
              </div>
              <Button onClick={saveBase} disabled={!newName.trim()} data-testid="save-base-btn">{editingId ? t("Opslaan") : t("Aanmaken")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* DE HOOGTEGRENS OP DE TELEFOON IS RUIMER, EN DIT IS EEN GEMETEN
            GEBREK EN GEEN SMAAK. Franks melding van 23 augustus 2026: onder
            GLOBAL stond alleen "Resources, 17 documents", en de banken van de
            Brain-import leken verdwenen.

            ZE WAREN ER WEL. Hier stond `max-h-40`, tien rem oftewel 160 pixels,
            en die gold ALLEEN onder `md`, dus alleen op een telefoon. De hele
            banklijst zat in een scrollvakje van 160 pixels; met de kop en de
            plusknop erin past er precies EEN regel, en niets verraadt dat er
            nog iets onder staat. Gemeten in de database: er zijn 8 globale en
            42 projectbanken, alle vijftig van deze gebruiker, en de route geeft
            ze alle vijftig terug. Er werd dus niets gefilterd; er werd geknepen.

            WAAROM HET NOOIT EERDER OPVIEL: op de Mac gold `md:max-h-none`, dus
            daar staan ze gewoon onder elkaar. Een gebrek dat alleen op glas van
            390 pixels bestaat, en dat is precies de controle die ik zelf niet
            kan doen; zie de opruimlijst.

            `max-h-[60vh]` EN NIET ONBEGRENSD, want de lijst mag het scherm ook
            niet helemaal opeten: het paneel eronder moet bereikbaar blijven
            zonder eerst langs vijftig banken te scrollen. Zestig procent van de
            hoogte is op een iPhone ruim 500 pixels, dus een stuk of tien regels
            in plaats van een. */}
        <aside className="w-full md:w-72 border-b md:border-b-0 md:border-r overflow-y-auto p-3 space-y-2 max-h-[60vh] md:max-h-none shrink-0">
          {/* FIX 7: Afkortingen bovenaan, boven Global. Franks glasronde van 11 oktober 2026: hij kon het paneel niet vinden. */}
          {/* GBRAIN PUNT 75 (post d): eigen afkortingen voor het zoeken. */}
          <AliassenPaneel />
          {/* Vondst Frank 27 okt 2026: het volledige overzicht van wat niet is ingelezen, op een vaste plek. */}
          <NietIngelezenPaneel />
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              {/* Global first, because it weighs in on every chat, and always
                  present even when empty. Frank's call on 30 July, and his
                  reason: a feature you cannot find costs more than a heading
                  you do not need. */}
              <section data-testid="scope-group-global" className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("Algemeen")}</h2>
                  <Button
                    variant="ghost" size="icon" data-testid="new-global-base-btn"
                    aria-label={t("Nieuwe algemene kennisbank")}
                    className="tap-target h-8 w-8 md:h-6 md:w-6"
                    onClick={() => openCreate("")}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                {globalBases.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1 pb-2">{t("Hier staat nog niets. Een algemene kennisbank telt mee in elk gesprek.")}</p>
                ) : globalBases.map(renderBase)}
              </section>

              <section data-testid="scope-group-project" className="space-y-2 pt-2">
                <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground px-1">{t("Projecten")}</h2>
                {projectBases.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1">{t("Nog geen kennisbanken bij een project.")}</p>
                ) : projectBases.map(renderBase)}
              </section>
            </>
          )}
        </aside>

        <main className="flex-1 flex flex-col min-w-0 p-4 overflow-y-auto">
          {!selectedBase ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center"><BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>{t("Kies een kennisbank")}</p></div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-serif">{selectedBase.name}</h2>
                    {/* Also here, and not only in the list: this is the header
                        you are looking at while you upload into it, and what a
                        base holds decides where those files end up. */}
                    <KindBadge kb={selectedBase} />
                  </div>
                  <p className="text-sm text-muted-foreground">{selectedBase.description || t("Geen omschrijving")}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { const el = document.createElement('input'); el.type = 'file'; el.multiple = true; el.accept = '.txt,.md,.pdf,.docx,.xlsx,.pptx,.py,.js,.ts,.jsx,.tsx,.css,.html,.json,.csv,.md,.rs,.go,.java,.cpp,.c,.rb,.sql,.sh,.yaml,.yml,.toml,.ini,.cfg'; el.onchange = handleUpload; el.click(); }} disabled={uploading}>
                    {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}{t("Uploaden")}
                  </Button>
                  {/* Alleen zichtbaar zodra deze bank een bron heeft. Een knop
                      die altijd staat en meestal een fout geeft, leert je hem
                      te negeren. */}
                  {selectedBase.source_folder && (
                    <Button variant="outline" size="sm" onClick={reindex} disabled={importing} data-testid="reindex">
                      {importing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}{t("Opnieuw indexeren")}
                    </Button>
                  )}
                  {/* W1k: intern, voor elke bank. */}
                  <Button variant="outline" size="sm" role="switch" aria-checked={Boolean(selectedBase.intern)}
                          onClick={toggleIntern} data-testid="toggle-intern"
                          title={t("Een interne bank gaat nooit naar een koppeling buiten de EU of met een onbekende plek.")}>
                    <Lock className="h-3 w-3 mr-1" />{selectedBase.intern ? t("Intern") : t("Niet intern")}
                  </Button>
                  {/* R18. Alleen bij een bank die een map HEEFT, om dezelfde
                      reden als Re-index hierboven. */}
                  {selectedBase.source_folder && (
                    <Button
                      variant="outline"
                      size="sm"
                      role="switch"
                      aria-checked={!selectedBase.sync_off}
                      onClick={toggleSync}
                      data-testid="toggle-sync"
                    >
                      {selectedBase.sync_off
                        ? <><PauseCircle className="h-3 w-3 mr-1" />{t("Automatisch bijwerken uit")}</>
                        : <><RefreshCw className="h-3 w-3 mr-1" />{t("Automatisch bijwerken aan")}</>}
                    </Button>
                  )}
                </div>
              </div>

              {/* WANNEER DE LUS KEEK EN WAT ER TOEN GEBEURDE, ALTIJD SAMEN.
                  Franks eis van 22 augustus 2026, en zij staat hier als een
                  ONMOGELIJKHEID en niet als een afspraak: dit component krijgt
                  de hele bank, en er bestaat geen component dat alleen de
                  tijdstempel toont. Zie FolderSyncStatus.jsx. */}
              <div className="mb-3">
                <FolderSyncStatus kb={selectedBase} />
              </div>

              {/* De mapimport. Een getypt pad en geen kiezer, en dat is een
                  bewuste grens: een webpagina kan de server geen maplocatie
                  geven, en `webkitdirectory` levert de BESTANDEN, wat een hele
                  vault door de browser zou sturen. De backend draait op deze
                  machine en leest de map zelf. Een echte kiezer hoort in de
                  Electron-schil en zit niet in deze fase. */}
              <div className="flex gap-3 mb-4">
                <Input
                  value={folderPath}
                  onChange={e => setFolderPath(e.target.value)}
                  placeholder={t("Map met notities, bijvoorbeeld /Users/jij/Vault")}
                  className="flex-1"
                  data-testid="folder-path"
                  onKeyDown={e => e.key === 'Enter' && importFolder()}
                />
                <Button variant="outline" onClick={importFolder} disabled={importing || !folderPath.trim()} data-testid="import-folder">
                  {importing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FolderInput className="h-3 w-3 mr-1" />}{t("Map importeren")}
                </Button>
              </div>

              {/* HET VERSLAG, en het blijft staan. Een import van 1285
                  bestanden waarin er drie stukliepen mag niet als "gelukt"
                  lezen, dus de mislukkingen staan er MET hun reden en niet als
                  een getal. */}
              {importReport && (
                <Card className="mb-4" data-testid="import-report">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">
                      {t("{gevonden} bestand(en) gelezen: {toegevoegd} nieuw, {bijgewerkt} bijgewerkt, {overgeslagen} ongewijzigd", {
                        gevonden: importReport.gevonden, toegevoegd: importReport.toegevoegd,
                        bijgewerkt: importReport.bijgewerkt, overgeslagen: importReport.overgeslagen })}
                    </CardTitle>
                  </CardHeader>
                  {importReport.mislukt?.length > 0 && (
                    <CardContent className="text-xs space-y-1">
                      <p className="text-destructive font-medium">
                        {t("{n} konden niet worden gelezen:", { n: importReport.mislukt.length })}
                      </p>
                      {importReport.mislukt.map((m, i) => (
                        <p key={i} className="text-muted-foreground">
                          <span className="font-mono">{m.pad}</span> - {m.reden}
                        </p>
                      ))}
                    </CardContent>
                  )}
                </Card>
              )}

              <div className="flex gap-3 mb-4">
                <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={t("Zoek in deze kennisbank…")} className="flex-1" onKeyDown={e => e.key === 'Enter' && handleSearch()} />
                <Button variant="outline" onClick={handleSearch} disabled={searching || !query.trim()}>
                  {searching ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}{t("Zoeken")}
                </Button>
              </div>

              {queryResults && (
                <Card className="mb-4">
                  <CardHeader className="py-3"><CardTitle className="text-sm">{t("Zoekresultaten ({n} stukken)", { n: queryResults.total_chunks })}</CardTitle></CardHeader>
                  <CardContent className="max-h-48 overflow-y-auto text-xs space-y-2">
                    {queryResults.chunks.map((c, i) => (
                      <div key={i} className="p-2 bg-muted/30 rounded">
                        <div className="text-muted-foreground mb-1">{t("[{f}] afstand: {d}", { f: c.filename, d: c.distance.toFixed(3) })}</div>
                        <div className="line-clamp-3">{c.content}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* ── DE BESTANDEN DIE NIET GELEZEN KONDEN WORDEN. PUNT 40. ──
                  Franks eis van 30 augustus 2026: "de 17 scans zichtbaar op
                  het bankscherm. Nu staat er error en ziet niemand dat."

                  PUNT 19 ZETTE DE REDEN AL ACHTER ELK DOCUMENT, en dat werkt
                  per REGEL. Bij 2353 documenten met 32 fouten ertussen is dat
                  onvindbaar: je moet de tabel afgaan om te weten of er iets
                  mis is. Deze lijst staat BOVEN de tabel, want een melding
                  eronder begint op 63 tot 83 procent van de tekst en wordt
                  niet gelezen (Franks meting van 25 augustus).

                  DE LIJST KOMT UIT `documents` EN NIET UIT EEN TWEEDE
                  AANROEP. Twee wegen naar hetzelfde getal kunnen het oneens
                  worden, en dan meldt de kop iets anders dan de regels
                  eronder. */}
              {documents.some(d => d.status === "error") ? (
                <div
                  className="border border-destructive/40 bg-destructive/5 rounded-lg p-3 mb-3"
                  data-testid="kb-unreadable-list"
                >
                  <p className="text-sm font-medium text-destructive">
                    {t("{fout} van {n} bestanden konden niet worden gelezen, dus hun inhoud is niet doorzoekbaar.", { fout: documents.filter(d => d.status === "error").length, n: documents.length })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("Een PDF zonder tekst is bijna altijd een scan. Van deze bestanden is niets opgeslagen, dus een vraag over wat erin staat kan deze kennisbank niet beantwoorden.")}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {documents.filter(d => d.status === "error").map(d => (
                      <li
                        key={d.id}
                        className="text-xs"
                        data-testid={`kb-unreadable-file-${d.id}`}
                      >
                        <span className="font-medium">{d.filename}</span>
                        {d.error ? (
                          <span className="text-muted-foreground"> - {d.error}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* Six columns do not fit on a phone. The table scrolls
                  inside this box rather than pushing the page sideways. */}
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Bestandsnaam")}</TableHead>
                      <TableHead>{t("Soort")}</TableHead>
                      <TableHead>{t("Grootte")}</TableHead>
                      <TableHead>{t("Stukken")}</TableHead>
                      <TableHead>{t("Status")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t("Nog geen documenten. Upload bestanden om ze te indexeren.")}</TableCell></TableRow>
                    ) : documents.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">
                          {/* Same as the project screen: the name opens the
                              document. A document that can only be counted and
                              deleted is a filename, not a document. */}
                          <button
                            type="button"
                            onClick={() => setViewingDocument(d)}
                            data-testid={`knowledge-doc-open-${d.id}`}
                            className="text-left hover:underline"
                          >
                            <FileText className="h-3 w-3 inline mr-1 text-muted-foreground" />{d.filename}
                          </button>
                          {/* A document can be here and not count in the
                              project this base belongs to. Saying nothing let
                              someone conclude the opposite, which is the one
                              state a file may never be left in silently. */}
                          {d.in_project === false && (
                            <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`knowledge-doc-out-${d.id}`}>
                              <span className="text-xs text-muted-foreground">
                                {t("uit {p} gehaald", { p: d.project_name || t("zijn project") })}
                              </span>
                              <button
                                type="button"
                                onClick={() => putBack(d)}
                                data-testid={`knowledge-doc-putback-${d.id}`}
                                className="text-xs underline text-[hsl(var(--accent))]"
                              >
                                {t("zet terug")}
                              </button>
                            </div>
                          )}
                          {/* The reason, as a line and not as a tooltip. A
                              `title` needs a mouse to hover, so on a phone the
                              only thing that ever appeared was a red cross. */}
                          {d.status === "error" && d.error ? (
                            <div className="mt-1 text-xs text-destructive" data-testid={`knowledge-doc-error-${d.id}`}>
                              {d.error}
                            </div>
                          ) : null}
                          {/* Alleen bij een SCHATTING. Exact is de normale gang
                              van zaken en verdient geen regel, en onbekend
                              (documenten van voor deze vlag) is niets om over
                              te melden. Zelfde vorm als de foutreden hierboven:
                              een regel en geen tooltip, want een `title` heeft
                              een muis nodig. */}
                          {d.token_count_exact === false ? (
                            <div className="mt-1 text-xs text-muted-foreground" data-testid={`knowledge-doc-estimated-${d.id}`}>
                              {t("Opgeknipt met een geschatte tokentelling, dus de stukken hebben mogelijk niet de bedoelde grootte.")}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs">{d.content_type}</TableCell>
                        <TableCell>{(d.size_bytes / 1024).toFixed(1)} KB</TableCell>
                        <TableCell>{d.chunk_count}</TableCell>
                        <TableCell>{d.status === "ready" ? <CheckCircle className="h-4 w-4 text-ok" /> : d.status === "error" ? <XCircle className="h-4 w-4 text-destructive" title={d.error} /> : <Loader2 className="h-4 w-4 animate-spin" />}</TableCell>
                        <TableCell><Button variant="ghost" size="icon" className="tap-target h-7 w-7" onClick={() => deleteDoc(d.id)}><Trash2 className="h-3 w-3" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </main>
      </div>
      {viewingDocument && (
        <DocumentViewer document={viewingDocument} onClose={() => setViewingDocument(null)} />
      )}
      <MobileBottomNav />
    </div>
  );
}
